import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { ChipToggleGroup, PulseChip, PulseRow } from '@/design-system'
import { useGalleryLibrary } from '@/features/album/hooks/useGalleryLibrary'
import { SpaceCard } from '../../components/SpaceCard'
import { SPACE_CATALOG, SpaceDefinition, SpaceStatus } from '../../spaceCatalog'

type StatusFilter = 'all' | SpaceStatus

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'walkable', label: 'Walkable' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'hub', label: 'Hub' },
]

export function SpacesLibraryScreen() {
  const navigate = useNavigate()
  const { galleries, loading } = useGalleryLibrary()
  const [status, setStatus] = useState<StatusFilter>('all')

  const filtered = useMemo(() => {
    if (status === 'all') return SPACE_CATALOG
    return SPACE_CATALOG.filter((s) => s.status === status)
  }, [status])

  const hostGallery = useMemo(() => {
    return galleries.reduce<(typeof galleries)[number] | null>((latest, g) => {
      if (!g.updatedAt) return latest ?? g
      if (!latest?.updatedAt || g.updatedAt > latest.updatedAt) return g
      return latest
    }, null)
  }, [galleries])

  const openSpace = (space: SpaceDefinition) => {
    if (!hostGallery) {
      toast.error('Create a 3D gallery first — Spaces open on one of your galleries')
      navigate('/galleries')
      return
    }
    navigate(`/galleries/${hostGallery.id}/${space.path}`)
  }

  return (
    <Box className='ds-fade-up'>
      <Box display='flex' alignItems='flex-end' justifyContent='space-between' mb='6px' gap={2} flexWrap='wrap'>
        <Box>
          <Typography component='h1' sx={{ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em' }}>
            Spaces
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'var(--ds-ink-subtle)', mt: '3px', maxWidth: '46ch' }}>
            Walkable places beyond the print wall — real Studio prototypes, opened on one of your galleries.
          </Typography>
        </Box>
      </Box>

      <PulseRow>
        <PulseChip dotColor='var(--ds-accent-dot-4, #01dafe)'>
          <b>Present Hall</b>&nbsp;· Studio ◇ HALL
        </PulseChip>
        <PulseChip dotColor='var(--ds-accent-dot-1, #ffcf7d)'>
          <b>Mountain Lot</b>&nbsp;· Shop living template
        </PulseChip>
        <PulseChip dotColor='var(--ds-accent-dot-3, #be9dfd)'>
          {hostGallery ? (
            <>
              Opens on&nbsp;<b>{hostGallery.title}</b>
            </>
          ) : loading ? (
            <>Looking for a gallery…</>
          ) : (
            <>
              Needs a&nbsp;<b>3D gallery</b>&nbsp;first
            </>
          )}
        </PulseChip>
      </PulseRow>

      <Box display='flex' alignItems='center' gap={1} pb='20px' flexWrap='wrap'>
        <ChipToggleGroup
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setStatus(v as StatusFilter)}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
        }}
      >
        {filtered.map((space, i) => (
          <SpaceCard key={space.id} space={space} index={i} onClick={() => openSpace(space)} />
        ))}
      </Box>
    </Box>
  )
}
